import { Banner, Box, Button, Divider, Tag, Typography } from "@luce/ui";

import type { GiftCard } from "../lib";
import { formatExpiry } from "../lib";
import { GiftCardHero } from "./gift-card-hero";

interface ReceivedGiftDetailProps {
  giftCard: GiftCard;
  redeemed: boolean;
  onCheckCreditAccount: () => void;
}

export function ReceivedGiftDetail({
  giftCard,
  redeemed,
  onCheckCreditAccount,
}: ReceivedGiftDetailProps) {
  const textColor = redeemed ? "neutral-foreground-default" : "neutral-foreground-attenuated";
  const supportingColor = redeemed
    ? "neutral-foreground-intermediate"
    : "neutral-foreground-attenuated";

  return (
    <Box className="gap-spacing-md flex flex-col">
      {!redeemed && (
        <Banner
          variant="info"
          description="This gift card is no longer available because it has passed its expiration date."
        />
      )}

      <Box
        className={
          redeemed
            ? "border-luce-brand-primaryBorder shadow-card overflow-hidden rounded-2xl border bg-white"
            : "border-luce-neutral-border-mediumEmphasis shadow-card overflow-hidden rounded-2xl border bg-white"
        }
      >
        <Box className="p-spacing-lg">
          <GiftCardHero occasion={giftCard.occasionType} muted={!redeemed} />
        </Box>

        <Box className="gap-spacing-md px-spacing-lg pb-spacing-md flex flex-row items-center">
          <Box className="gap-spacing-xs flex flex-1 flex-col">
            <Typography
              variant="heading-h3"
              color={redeemed ? "system-success-default" : "neutral-foreground-attenuated"}
            >
              {redeemed ? `$${giftCard.amount} Gift Received` : `$${giftCard.amount}`}
            </Typography>
            {giftCard.expirationDate && (
              <Typography variant="body-sm" color={supportingColor}>
                Expires on {formatExpiry(giftCard.expirationDate)}
              </Typography>
            )}
          </Box>
          {!redeemed && <Tag variant="neutral">Expired</Tag>}
        </Box>

        <Divider />

        <Box className="gap-spacing-xs px-spacing-lg py-spacing-md flex flex-col">
          <Typography variant="body-md" color={textColor}>
            Message from {giftCard.senderName}
          </Typography>
          <Typography variant="label-md" color={textColor}>
            {giftCard.messageTitle}
          </Typography>
          <Typography variant="body-sm" color={supportingColor}>
            {giftCard.messageBody}
          </Typography>
        </Box>
      </Box>

      {redeemed && (
        <Box className="pt-spacing-md flex flex-row justify-end">
          <Button
            variant="primary"
            size="lg"
            label="Check Credit Account"
            onClick={onCheckCreditAccount}
          />
        </Box>
      )}
    </Box>
  );
}
